import { ArrowLinkText } from '@/components/common/ArrowLinkText';
import ButtonWithIcon from '@/components/common/ButtonWithIcon';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import { ArrowRight, File06 } from '@/components/common/icons';
import Icon from '@/components/common/icons/Icon';
import QuestionAndAnswer from '@/components/common/QuestionAndAnswer';
import { hoverStyleProps } from '@/components/common/styleHelpers';
import TwoColumnLayout from '@/components/common/TwoColumnLayout';
import { LinkText, Paragraph, Text } from '@/components/common/Typography';
import {
    Box,
    BoxProps,
    Divider,
    ListItem,
    OrderedList,
} from '@chakra-ui/react';
import { iconNameByIraType } from '@common/react/goals';
import { IconName } from '@common/react/icons';
import { isString } from '@common/util/types';
import { ReactNode } from 'react';
import Balancer from 'react-wrap-balancer';
import OpenableRetirementAccountList from './OpenableRetirementAccountList';
import RetirementAccountList from './RetirementAccountList';
import { useRetirementGoalContext } from './RetirementGoalDetailContainer';
import { showWhatIsAnIRAModal } from './WhatIsAnIRAModal';

// Hide goal statement until the implementation is finished
const isGoalStatementEnabled = false;

const LowerContent = () => {
    const { isSharedGoal, partnerSwitcherType } = useRetirementGoalContext();

    switch (partnerSwitcherType) {
        case 'MINE':
            return (
                <FlexCol mb={10}>
                    <TwoColumnLayout
                        left={<RetirementAccountList />}
                        right={<OpenableRetirementAccountList />}
                    />
                    {isGoalStatementEnabled && !isSharedGoal && (
                        <>
                            <Divider my={12} />
                            <GoalStatementSection />
                        </>
                    )}
                    <Divider my={12} />
                    <ExplainerSection />
                    <Divider my={12} />
                    <QuestionAndAnswerSection />
                </FlexCol>
            );
        case 'SHARED':
            return (
                <FlexCol mb={10}>
                    {isGoalStatementEnabled && (
                        <>
                            <GoalStatementSection />
                            <Divider my={12} />
                        </>
                    )}
                    <TwoColumnLayout
                        left={
                            <FlexCol>
                                <Text fontWeight={600} fontSize="md" mb={8}>
                                    Commonly asked questions
                                </Text>
                                <QuestionAndAnswerSection />
                            </FlexCol>
                        }
                        right={
                            // TODO: Consider reimplementing combined contribution section
                            <></>
                        }
                    />
                </FlexCol>
            );
        case 'THEIRS':
            return (
                <FlexCol mb={10}>
                    <TwoColumnLayout
                        left={<RetirementAccountList />}
                        right={<></>}
                    />
                </FlexCol>
            );
    }
};

export default LowerContent;

interface GoalStatementSectionProps extends BoxProps {}

const GoalStatementSection = ({ ...props }: GoalStatementSectionProps) => {
    const { goal, goalConfig } = useRetirementGoalContext();
    const iOrWe = goal.shareStatus === 'SHARED' ? 'We' : 'I';
    // TODO: Move this into the goal config when building out the logic
    // https://linear.app/plenty/issue/PLE-766
    const parts = [
        `${iOrWe} want to have $`,
        { type: 'FIELD', placeholderLength: 10 },
        'to retire with a',
        { type: 'FIELD', placeholderLength: 20 },
        'and',
        { type: 'FIELD', placeholderLength: 16 },
        'of travel by',
        { type: 'FIELD', placeholderLength: 10 },
    ];

    return (
        <Box {...props}>
            <Balancer>
                <Text fontSize="xl" fontWeight={300} lineHeight="taller">
                    {parts.map((part, idx) =>
                        isString(part) ? (
                            ' ' + part + ' '
                        ) : (
                            <LinkText
                                key={idx}
                                color={goalConfig.color}
                                {...hoverStyleProps({
                                    color: goalConfig.color,
                                    opacity: 0.8,
                                })}
                                fontWeight={700}
                                px={1}
                                // TODO: onClick={() => showGoalStatementModal({ goal })}
                            >
                                {'_'.repeat(part.placeholderLength)}
                            </LinkText>
                        )
                    )}
                </Text>
            </Balancer>
        </Box>
    );
};

const ExplainerSection = () => {
    const {
        goalConfig,
        setPosition,
        myPlentyTraditionalIraStatus,
        myPlentyRothIraStatus,
    } = useRetirementGoalContext();
    const label = 'What is an IRA?';

    return (
        <FlexCol>
            <FlexRow justifyContent="space-between" alignItems="center" mb={8}>
                <Text fontWeight={600} fontSize="md">
                    Which kind of IRA is right for me?
                </Text>
                <ButtonWithIcon
                    variant="ghost"
                    colorScheme="gray"
                    iconLeft={<File06 />}
                    size="sm"
                    onClick={() => showWhatIsAnIRAModal({})}
                    onClickEvent="IRA Info Button Clicked"
                    onClickEventParams={{
                        label,
                        location: 'Retirement Goal Detail',
                    }}
                >
                    {label}
                </ButtonWithIcon>
            </FlexRow>
            <FlexRow w="full" gap={10}>
                <ExplainerSectionItem
                    iconName={iconNameByIraType['TRADITIONAL']}
                    iconColor={goalConfig.color}
                    onClickLearnMore={
                        myPlentyTraditionalIraStatus === 'NOT_STARTED'
                            ? () => setPosition('OPEN_TRADITIONAL_IRA')
                            : undefined
                    }
                >
                    <Text fontSize="sm" fontWeight={600} color="textDark">
                        A Traditional IRA
                    </Text>{' '}
                    may allow you to make pre-tax contributions, depending on
                    your employer and your income level. It’s best suited for an
                    individual who expects to be in the same or lower tax
                    bracket when they withdraw at retirement.
                </ExplainerSectionItem>
                <ExplainerSectionItem
                    iconName={iconNameByIraType['ROTH']}
                    iconColor={goalConfig.color}
                    onClickLearnMore={
                        myPlentyRothIraStatus === 'NOT_STARTED'
                            ? () => setPosition('OPEN_ROTH_IRA')
                            : undefined
                    }
                >
                    <Text fontSize="sm" fontWeight={600} color="textDark">
                        A Roth IRA
                    </Text>{' '}
                    allows you to make after-tax contributions, if you are below
                    a specific income level. It’s best suited for an individual
                    who expects to be in a higher tax bracket when they start
                    withdraw at retirement.
                </ExplainerSectionItem>
            </FlexRow>
        </FlexCol>
    );
};

interface ExplainerSectionItemProps {
    children: ReactNode;
    iconName: IconName;
    iconColor: string;
    onClickLearnMore: VoidFunction | undefined;
}

export const ExplainerSectionItem = ({
    children,
    iconName,
    iconColor,
    onClickLearnMore,
}: ExplainerSectionItemProps) => {
    return (
        <FlexRow w="full">
            <Icon name={iconName} size="lg" color={iconColor} mt={1} ml={1} />
            <FlexCol ml={3}>
                <Paragraph fontSize="sm" lineHeight="taller" color="textMuted">
                    {children}
                </Paragraph>
                {onClickLearnMore && (
                    <ArrowLinkText mt={2} onClick={onClickLearnMore}>
                        Learn more
                    </ArrowLinkText>
                )}
            </FlexCol>
        </FlexRow>
    );
};

const QuestionAndAnswerSection = () => {
    return (
        <QuestionAndAnswer
            canOpenMultiple
            items={[
                {
                    question:
                        'What should I consider when planning for retirement?',
                    answer: (
                        <>
                            <Paragraph>
                                We believe the three most important factors to
                                consider are:
                            </Paragraph>
                            <Paragraph mt={2}>
                                <OrderedList>
                                    <ListItem>
                                        When do you want to retire?
                                    </ListItem>
                                    <ListItem>
                                        How much should you save?
                                    </ListItem>
                                    <ListItem>What do you want to do?</ListItem>
                                </OrderedList>
                            </Paragraph>
                            <LinkText
                                mt={4}
                                href="https://www.withplenty.com/blog/retirement-planning-three-things-to-consider"
                                target="_blank"
                            >
                                Read our deep dive on the Plenty blog
                                <ArrowRight size="sm" />
                            </LinkText>
                        </>
                    ),
                },
                {
                    question: 'What kind of investments are available?',
                    answer: (
                        <>
                            <Paragraph>
                                Plenty’s retirement portfolios are invested in
                                US domestic stocks, international stocks, bonds,
                                and treasuries. US domestic stocks are held
                                directly, meaning we save on fees and don’t use
                                ETFs. Coming soon, we’ll be bringing access to
                                alternative assets like venture / private equity
                                funds to your Plenty retirement account.
                            </Paragraph>
                        </>
                    ),
                },
                {
                    question: 'What are Plenty’s fees?',
                    answer: (
                        <>
                            <Paragraph>
                                Plenty charges 0.20% for investment management
                                and creates blended portfolios with expense
                                ratios that range from 0.06% - 0.13%, meaning
                                the all in expense is 0.26% - 0.33%. There are
                                no additional costs or fees for the values-based
                                customization for your portfolio.
                            </Paragraph>
                            <Paragraph mt={2}>
                                In comparison, 401k plan fees are usually 0.5%
                                to 2% per year (source: Investopedia 2021). If
                                you’re considering other roboadvisors, make sure
                                you’re adding the management fee{' '}
                                <Text fontStyle="italic">plus</Text> the expense
                                ratios for their portfolios.
                            </Paragraph>
                        </>
                    ),
                },
                {
                    question:
                        'Is Plenty’s retirement account secure and insured?',
                    answer: (
                        <Paragraph>
                            Your money is owned by you and held by BNY Mellon |
                            Pershing; a global leader with $2T in assets as of
                            March 2023. Your retirement account is protected and
                            insured up to $500,000 per account through the SIPC
                            (Securities Investor Protection Corporation).
                        </Paragraph>
                    ),
                },
                {
                    question:
                        'Can I move other retirement accounts into Plenty?',
                    answer: (
                        <Paragraph>
                            Yes, soon! This is on our roadmap and we’re working
                            to make this process as simple for you as possible.
                        </Paragraph>
                    ),
                },
                {
                    question: 'Can I move my retirement account out of Plenty?',
                    answer: (
                        <Paragraph>
                            If you’d like to transfer your account to another
                            institution, you can do that anytime by contacting
                            our team at priority@withplenty.com.
                        </Paragraph>
                    ),
                },
            ]}
        />
    );
};
